import { useEffect, useRef, useMemo } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Users, BarChart3, House, Search } from 'lucide-react';
import { useMyProfile } from '@/features/profile/hooks/useMyProfile';
import { NewExpenseFAB } from '@/shared/components/NewExpenseFAB';
import { TopBar } from '@/shared/components/TopBar';
import { UpdatePrompt, UpdateProvider } from '@/shared/components/UpdatePrompt';
import { OfflineBanner } from '@/shared/components/OfflineBanner';
import { InstallPrompt } from '@/features/install/InstallPrompt';
import { AndroidAppLink } from '@/features/install/AndroidAppLink';
import { detectPlatform } from '@/features/install/useInstallPWA';
import { ensureLocaleLoaded, normalizeToSupported } from '@/i18n';
import { setIsDemoUser } from '@/shared/lib/demoGuard';
import { DemoBanner } from '@/shared/components/DemoBanner';
import { useKeepAlive } from '@/shared/hooks/useKeepAlive';
import { useOfflineQueue } from '@/shared/hooks/useOfflineQueue';
import { VersionModal } from '@/features/releases/components/VersionModal';
import { ConsentGate } from '@/features/auth/components/ConsentGate';
import { SystemMessageBanner } from '@/features/releases/components/SystemMessageBanner';
import { SearchPaletteProvider, useSearchPalette } from '@/features/search/SearchPaletteProvider';
import { useNotificationsRealtime } from '@/features/notifications/hooks/useNotificationsRealtime';
import { useRealtimeSync } from '@/features/realtime/useRealtimeSync';
import { AppShell } from '@/shared/layout/AppShell';
import { useGlobalShortcuts } from '@/shared/hooks/useGlobalShortcuts';
import { ShortcutsHelp } from '@/shared/components/ShortcutsHelp';

export function App() {
  const { i18n } = useTranslation();
  const { data: profile } = useMyProfile();

  // Sync `preferred_locale` de la BD → i18n **una sola vez** cuando el
  // profile llega por primera vez. Después de eso no se vuelve a tocar el
  // idioma: si alguien lo cambia en Perfil, su elección gana. Antes esta
  // effect corría en cada re-fetch del profile y "restauraba" el idioma de la
  // BD, haciendo que el cambio manual pareciera un no-op ("dice ok pero sigue
  // en español").
  // El demo se salta el sync (no puede escribir en BD por RLS).
  const syncedRef = useRef(false);
  useEffect(() => {
    if (syncedRef.current) return;
    if (!profile || profile.is_demo) return;
    if (!profile.preferred_locale) return;
    const wanted = normalizeToSupported(profile.preferred_locale);
    // **Sólo se adopta un idioma que esta superficie tenga de verdad.** La
    // columna la comparte el cliente nativo, que desde el 2026-08-21 tiene ocho
    // idiomas contra los siete de aquí (#48), así que puede traer `pt`.
    //
    // Cuando entró (#48) esto evitaba algo peor que enseñarlo en otro idioma:
    // lo **borraba**. `normalizeToSupported('pt')` daba `en`, el
    // `changeLanguage` de abajo disparaba `languageChanged` y el listener de
    // persistencia escribía ese `en` en la columna. Ese listener ya no existe
    // (#50, más abajo), así que hoy la guarda sólo decide qué se pinta — y sigue
    // haciendo falta: sin ella, a quien tenga `pt` se le fuerza el inglés
    // aunque su navegador esté en algo que esta superficie sí tiene.
    if (wanted !== profile.preferred_locale) return;
    if (i18n.language !== wanted) {
      // Cargar el bundle si es un locale lazy antes de aplicar.
      void ensureLocaleLoaded(wanted).then(() => i18n.changeLanguage(wanted));
    }
    syncedRef.current = true;
  }, [profile?.preferred_locale, profile?.is_demo, i18n]);

  // La plataforma no cambia mientras la pestaña vive, así que se mira una vez
  // y no en cada render.
  const isAndroid = useMemo(() => detectPlatform() === 'android', []);

  // Poblar la flag demo para el guardian global de mutations. Consumido por
  // el mutationCache.onError en providers.tsx.
  useEffect(() => {
    setIsDemoUser(Boolean(profile?.is_demo));
  }, [profile?.is_demo]);

  // Keep-alive `last_active_at` cada 2 min mientras el tab esté visible.
  useKeepAlive(profile?.id, Boolean(profile?.is_demo));

  // Sprint I: monta el sync automático de la cola offline (createExpense
  // encolados en IndexedDB durante offline). El hook publica el count al
  // <OfflineBanner> vía suscripción interna.
  useOfflineQueue();

  // Sprint K.0: campanita en tiempo real. Suscribe el canal Realtime de
  // `notifications` filtrado por mi participant_id → el badge de no-leídas
  // reacciona al instante (el polling de 60s queda como red de seguridad).
  useNotificationsRealtime(profile?.id);

  // Tiempo real de gastos y grupos: cambios de otros miembros aparecen en vivo.
  useRealtimeSync(profile?.id);

  // Atajos de teclado. Van aquí y no en el armazón porque necesitan estar
  // dentro del router: `g h` navega.
  const { helpOpen, closeHelp } = useGlobalShortcuts();

  // Aquí había un listener de `languageChanged` que escribía **cualquier**
  // cambio de idioma en `preferred_locale`. Se fue el 2026-08-28 (#50), y lo
  // que importa es por qué:
  //
  // `changeLanguage` no distingue quién lo llamó. Lo llama una persona en el
  // selector del perfil, sí, pero también lo llama el arranque cuando el
  // idioma detectado es uno de los seis perezosos (`i18n/index.ts`), y ese
  // aterriza después de que el perfil haya llegado. O sea que un navegador en
  // gallego dejaba `preferred_locale = 'gl'` escrito sin que nadie eligiera
  // nada, y la columna dejaba de poder distinguir «elegí gallego» de «pasé por
  // aquí». Es la razón por la que el cliente nativo **no** la adopta.
  //
  // El arreglo no es una guarda más: es que dentro del armazón el único sitio
  // donde una persona elige idioma es `ProfilePage`, y ése ya escribe él mismo.
  // El listener era una segunda vía para lo mismo, y de paso una vía para lo
  // que no era una elección. Se borra y quedan cero escrituras fantasma.
  //
  // El comentario que había aquí defendía el listener diciendo que sin él «el
  // selector global sólo tocaría localStorage». Ese selector global ya no vive
  // dentro del armazón: `PublicLanguageSelector` se monta en las rutas de
  // acceso y en las legales, donde este componente no existe. El motivo había
  // caducado antes que el código.

  return (
    <UpdateProvider>
    <SearchPaletteProvider>
    <AppShell bottomNav={<BottomNav />}>
    <div
      className="flex min-h-[100dvh] flex-col bg-surface"
      style={{
        // Reservamos aquí el espacio del TopBar (fixed) para que
        // OfflineBanner + DemoBanner queden visibles JUSTO debajo,
        // no ocultos por el TopBar mientras ocupan hueco en flow.
        paddingTop: 'calc(env(safe-area-inset-top) + 2rem)',
      }}
    >
      <TopBar />
      <OfflineBanner />
      {profile?.is_demo && <DemoBanner />}
      <SystemMessageBanner />

      {/* El mismo enlace, en móvil y **sólo en Android**: en un iPhone sería un
          enlace a algo que no se puede instalar. En escritorio no se monta aquí
          porque ya está en la lateral, que es donde pediste que estuviera. */}
      {isAndroid && (
        <div className="lg:hidden">
          <AndroidAppLink className="border-b border-neutral-200 px-3 py-1" />
        </div>
      )}

      <main
        className="flex-1"
        style={{
          // El hueco de abajo es el alto de la barra inferior más un respiro.
          // `--nav-inferior` lo declara el armazón y vale 0 en escritorio, donde
          // no hay barra: reservarlo dejaría un vacío al final de cada página.
          paddingBottom: 'calc(env(safe-area-inset-bottom) + var(--nav-inferior) + 17px)',
        }}
      >
        <Outlet />
      </main>

      <NewExpenseFAB />
      <UpdatePrompt />
      <InstallPrompt />
      <VersionModal />
      {/* La puerta del consentimiento para cuentas que nunca lo dieron. Va aquí,
          dentro de la zona con sesión, porque necesita el perfil: sin cuenta no
          hay a quién preguntarle. */}
      <ConsentGate profile={profile} />
      <ShortcutsHelp open={helpOpen} onClose={closeHelp} />
    </div>
    </AppShell>
    </SearchPaletteProvider>
    </UpdateProvider>
  );
}

/**
 * La barra inferior de siempre, intacta. La monta `AppShell` sólo por debajo de
 * `lg`; en escritorio manda la lateral.
 */
function BottomNav() {
  const { t } = useTranslation();
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="mx-auto flex max-w-lg">
        <NavItem to="/" icon={<House size={20} />} label={t('nav.home')} />
        <NavItem to="/groups" icon={<Users size={20} />} label={t('nav.groups')} />
        <SearchNavItem />
        <NavItem to="/stats" icon={<BarChart3 size={20} />} label={t('nav.stats')} />
        <NavItem to="/profile" icon={<User size={20} />} label={t('nav.profile')} />
      </ul>
    </nav>
  );
}

/**
 * Item de nav que abre el SearchPalette en vez de navegar a una URL.
 * Sustituye al antiguo "Activity" (redundante con Home).
 */
function SearchNavItem() {
  const { t } = useTranslation();
  const searchPalette = useSearchPalette();
  return (
    <li className="flex-1">
      <button
        type="button"
        onClick={searchPalette.open}
        className="text-muted hover:text-ink flex w-full flex-col items-center gap-0.5 py-2 text-xs transition-colors"
      >
        <Search size={20} />
        <span>{t('nav.search', { defaultValue: 'Buscar' })}</span>
      </button>
    </li>
  );
}

function NavItem({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <li className="flex-1">
      <NavLink
        to={to}
        end={to === '/'}
        className={({ isActive }) =>
          `flex flex-col items-center gap-0.5 py-2 text-xs transition-colors ${
            isActive ? 'text-brand' : 'text-muted hover:text-ink'
          }`
        }
      >
        {icon}
        <span>{label}</span>
      </NavLink>
    </li>
  );
}
